import {
  type SaveExportPresetInput,
  type UserExportPreset,
  userExportPresetListSchema,
  userExportPresetSchema,
} from '@mss_creator/shared';
import { z } from 'zod';
import { request } from './api';

/**
 * Personal export presets (issue #13). Kept out of `api.ts` so chantier 5
 * touches no shared file there: it only reuses the typed `request` wrapper,
 * which handles auth, the error envelope and the 401 → login redirect.
 *
 * `request` types its schema as `ZodType<T>` (input = output), which a schema
 * carrying `.default(...)` fields — the table block's arrangement fields —
 * cannot satisfy. So the body is fetched as `unknown` and parsed here with
 * the real schema, which still validates every response at the boundary.
 *
 * The owner is always the signed-in user — the backend scopes every route to
 * `/me`, so no user id ever travels from here.
 */
export const exportPresetsApi = {
  list: async (): Promise<UserExportPreset[]> =>
    userExportPresetListSchema.parse(await request('/me/export-presets', z.unknown())),
  /** Upsert by trimmed name: re-saving an existing name overwrites it and keeps its id. */
  save: async (input: SaveExportPresetInput): Promise<UserExportPreset> =>
    userExportPresetSchema.parse(
      await request('/me/export-presets', z.unknown(), {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    ),
  /** Resolves on the backend's 204. */
  remove: (id: string): Promise<void> =>
    request(`/me/export-presets/${id}`, z.void(), { method: 'DELETE' }),
};
